import { useEffect, useRef } from 'react';

/**
 * Debounced autosave hook.
 * Calls onSave(data) after `delay` ms of inactivity.
 * Skips the first render.
 */
export function useAutosave(data, onSave, delay = 2000) {
  const timerRef    = useRef(null);
  const isFirstRun  = useRef(true);
  const onSaveRef   = useRef(onSave);
  const dataRef     = useRef(data);

  // Keep refs current
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  useEffect(() => { dataRef.current   = data;   }, [data]);

  useEffect(() => {
    // Skip first render — don't save on mount
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    // Reset timer on every data change
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onSaveRef.current(dataRef.current);
    }, delay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [data, delay]);

  // Flush immediately on unmount (tab switch / close)
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        onSaveRef.current(dataRef.current);
      }
    };
  }, []);
}
